import { useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Container,
  Group,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { Video } from '@nelnela/contracts';
import { api } from '../lib/api';
import { fmtTime, formatBytes } from '../lib/text';
import { useStudy } from '../app/study';
import { DueChip, FitBar, dueWordCount } from '../components/library/FitBar';

type Scope = 'shared' | 'mine';
const SCOPE_KEY = 'nn.library.scope';
const UNENTITLED_LINE = 'Adding videos is part of the paid layer.';

function readScope(): Scope {
  try {
    return sessionStorage.getItem(SCOPE_KEY) === 'mine' ? 'mine' : 'shared';
  } catch {
    return 'shared';
  }
}

/**
 * F2 Library (frames 3a mobile rows · 9b desktop 3-up grid): Shared · Mine, search (debounced),
 * "+" → /add, thumbnails from i.ytimg.com, one status chip per row when relevant. Unavailable
 * videos never arrive (the server filters); browsing emits no events. Fit bar / due chips are
 * M4 decorations — the card leaves room, renders nothing.
 *
 * M4c: a row whose provider is 'upload' (Mine only — an upload can never be shared) reads
 * "upload · 1.2 GB" where the channel name goes and gets a flat poster tile instead of a
 * YouTube thumbnail. The branch is on the row's own provider, not on whether the lane is
 * enabled, so nothing here changes for a learner who has no uploads: they have no such rows.
 */
export function Library() {
  const { entitled } = useStudy();
  const desktop = useMediaQuery('(min-width: 62em)', false, { getInitialValueInEffect: false });
  const [scope, setScope] = useState<Scope>(readScope);
  useEffect(() => {
    try {
      sessionStorage.setItem(SCOPE_KEY, scope);
    } catch {
      /* private mode */
    }
  }, [scope]);
  const [search, setSearch] = useState('');
  const [q] = useDebouncedValue(search.trim(), 250);
  /**
   * M6a (`3a`): "recent · best fit · most due". The two ranked orders are single-page — they sort
   * on a per-viewer decoration computed over the page, which the created_at cursor cannot encode
   * — so switching to one deliberately stops the infinite scroll rather than paginating wrongly.
   */
  const [sort, setSort] = useState<'recent' | 'fit' | 'due_density'>('recent');

  const query = useInfiniteQuery({
    queryKey: ['videos', scope, q, sort],
    queryFn: ({ pageParam }) =>
      api.videoList({
        scope,
        q: q || undefined,
        cursor: pageParam || undefined,
        ...(sort === 'recent' ? {} : { sort }),
      }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items: Video[] = query.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Container size={desktop ? 1100 : 640} py="lg" px="md">
      <Stack gap="md">
        <Group gap={4} wrap="nowrap" align="center">
          <Title order={2} style={{ flexShrink: 0 }}>
            Watch
          </Title>
          <TextInput
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder="search — latin ok"
            aria-label="Search videos"
            size="sm"
            style={{ flex: 1, minWidth: 0, marginLeft: 8 }}
            leftSection={<SearchGlyph />}
          />
          <UnstyledButton
            component={Link}
            to="/add"
            aria-label="Add a video"
            aria-disabled={!entitled || undefined}
            title={entitled ? 'Add a video' : UNENTITLED_LINE}
            onClick={(e: React.MouseEvent) => {
              if (!entitled) e.preventDefault();
            }}
            style={{
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: entitled ? 'var(--nn-text)' : 'var(--nn-faint)',
              cursor: entitled ? 'pointer' : 'default',
            }}
          >
            <PlusGlyph />
          </UnstyledButton>
        </Group>
        {!entitled && (
          <Text fz="xs" c="dimmed" mt={-8}>
            {UNENTITLED_LINE}
          </Text>
        )}

        <Group gap={8} align="center" wrap="nowrap">
          <SegmentedControl
            value={scope}
            onChange={(v) => setScope(v === 'mine' ? 'mine' : 'shared')}
            data={[
              { value: 'shared', label: 'Shared' },
              { value: 'mine', label: 'Mine' },
            ]}
            size="xs"
          />
          <span style={{ flex: 1 }} />
          {/* `3a` draws this as a plain text control with a chevron, not a second segmented
              control: the scope is the choice being made on this screen, and two controls of
              equal weight would say otherwise. */}
          {(['recent', 'fit', 'due_density'] as const).map((s) => (
            <UnstyledButton
              key={s}
              onClick={() => setSort(s)}
              style={{
                fontSize: 11.5,
                color: sort === s ? 'var(--nn-text-strong)' : 'var(--nn-muted)',
              }}
            >
              {s === 'recent' ? 'recent' : s === 'fit' ? 'best fit' : 'most due'}
            </UnstyledButton>
          ))}
        </Group>

        {query.isPending && (
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={desktop ? 'lg' : 12}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={desktop ? 200 : 54} radius="md" />
            ))}
          </SimpleGrid>
        )}
        {query.error && (
          <Text c="dimmed" fz="sm">
            Couldn’t load the library.
          </Text>
        )}
        {query.data && items.length === 0 && (
          <Text c="dimmed">
            {q ? (
              <>No videos match “{q}”.</>
            ) : scope === 'shared' ? (
              'Nothing in the shared library yet.'
            ) : (
              <>
                Videos you add appear here.{' '}
                {entitled && (
                  <Link to="/add" style={{ color: 'var(--nn-link)' }}>
                    Add a video
                  </Link>
                )}
              </>
            )}
          </Text>
        )}
        {items.length > 0 && (
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={desktop ? 'lg' : 12}>
            {items.map((v) => (
              <VideoCard key={v.id} video={v} scope={scope} stacked={!!desktop} />
            ))}
          </SimpleGrid>
        )}
        {query.hasNextPage && (
          <Button
            variant="default"
            onClick={() => void query.fetchNextPage()}
            loading={query.isFetchingNextPage}
            style={{ alignSelf: 'center' }}
          >
            Load more
          </Button>
        )}
      </Stack>
    </Container>
  );
}

// ------------------------------------------------ card

function VideoCard({ video: v, scope, stacked }: { video: Video; scope: Scope; stacked: boolean }) {
  const t = v.transcript;
  const job = t?.runningJob ?? null;
  const jobLabel =
    job && (job.status === 'queued' || job.status === 'running')
      ? job.kind === 'translate'
        ? 'translating…'
        : 'cleaning up…'
      : null;
  // M4c: an upload's `externalId` is a media uuid, not a YouTube id — the i.ytimg.com URL built
  // from it would 404 into a broken image, so these rows get a flat poster instead. Deliberately
  // not a real still: pulling one would mean a <video preload="metadata"> per card, i.e. the moov
  // atom of every file in the list. A still is a later nicety, not worth that on a phone.
  const upload = v.provider === 'upload';
  const due = dueWordCount(v);
  const thumb = upload
    ? null
    : `https://i.ytimg.com/vi/${encodeURIComponent(v.externalId)}/mqdefault.jpg`;
  const meta = [
    // where the channel name goes: an upload has no channel (and never can — that is what makes
    // sharing it impossible), so it names itself and its weight instead.
    upload
      ? v.mediaBytes != null
        ? `upload · ${formatBytes(v.mediaBytes)}`
        : 'upload'
      : (v.channel?.name ?? 'YouTube'),
    v.durationMs != null ? fmtTime(v.durationMs) : null,
    t ? `${t.lineCount} lines` : 'no transcript',
  ]
    .filter(Boolean)
    .join(' · ');
  const chips = (
    <Group gap={4} mt={2}>
      {jobLabel && (
        <Badge size="xs" variant="light" color="sky" tt="none" fw={500}>
          {jobLabel}
        </Badge>
      )}
      {!jobLabel && t && t.translationStatus === 'none' && (
        <Badge size="xs" variant="light" color="gray" tt="none" fw={500}>
          no EN yet
        </Badge>
      )}
      {scope === 'mine' && v.visibility === 'owner' && (
        <Badge size="xs" variant="outline" color="gray" tt="none" fw={500}>
          private
        </Badge>
      )}
      {/* M6a (`3a`): how much of this episode the learner already has, and how much of it is
          due. Both are per-viewer decorations and both are absent until the resolver has tagged
          the transcript, so a fresh video simply renders neither rather than an empty bar. */}
      {v.fit && <FitBar fit={v.fit} />}
      {due !== null && <DueChip n={due} />}
    </Group>
  );
  const title = (
    <Text
      fz={13.5}
      lh={1.45}
      lang="ka"
      style={{
        color: 'var(--nn-text-strong)',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
    >
      {v.title ?? v.externalId}
    </Text>
  );
  const duration =
    v.durationMs != null ? (
      <span
        style={{
          position: 'absolute',
          right: 4,
          bottom: 4,
          padding: '1px 5px',
          borderRadius: 4,
          background: 'rgba(0,0,0,.7)',
          fontSize: 9.5,
          color: '#fff',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {fmtTime(v.durationMs)}
      </span>
    ) : null;
  // Same box either way (black, 16:9 or 96×54, duration badge in its corner) — only what fills
  // it differs, so both layouts below drop this in where the <img> used to be.
  const poster = thumb ? (
    <img
      src={thumb}
      alt=""
      loading="lazy"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
    />
  ) : (
    <span
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'rgba(255,255,255,.42)', // on the tile's own black, not the theme's surface
      }}
    >
      <FilmGlyph size={stacked ? 30 : 20} />
    </span>
  );

  if (stacked) {
    return (
      <UnstyledButton
        component={Link}
        to={`/watch/${v.id}`}
        style={{ display: 'block', minWidth: 0 }}
        aria-label={v.title ?? v.externalId}
      >
        <div
          style={{
            position: 'relative',
            aspectRatio: '16 / 9',
            borderRadius: 7,
            overflow: 'hidden',
            background: '#000',
          }}
        >
          {poster}
          {duration}
        </div>
        <Stack gap={3} mt={8}>
          {title}
          <Text fz={11} c="dimmed">
            {meta}
          </Text>
          {chips}
        </Stack>
      </UnstyledButton>
    );
  }
  return (
    <UnstyledButton
      component={Link}
      to={`/watch/${v.id}`}
      style={{ display: 'flex', gap: 11, minWidth: 0, minHeight: 54, alignItems: 'flex-start' }}
      aria-label={v.title ?? v.externalId}
    >
      <span
        style={{
          position: 'relative',
          width: 96,
          height: 54,
          borderRadius: 7,
          overflow: 'hidden',
          background: '#000',
          flexShrink: 0,
          display: 'block',
        }}
      >
        {poster}
        {duration}
      </span>
      <Stack gap={3} style={{ minWidth: 0, flex: 1 }}>
        {title}
        <Text fz={11} c="dimmed">
          {meta}
        </Text>
        {chips}
      </Stack>
    </UnstyledButton>
  );
}

// ------------------------------------------------ glyphs (frame 3a header)

const SearchGlyph = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="11" cy="11" r="6.5" />
    <line x1="16" y1="16" x2="20.5" y2="20.5" />
  </svg>
);

/** M4c: what stands in for a thumbnail on an uploaded row — a frame with a play mark, in the
 *  same line-art register as the header glyphs. Sized by the caller: the two card layouts have
 *  very different tiles. */
const FilmGlyph = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3" y="5" width="18" height="14" rx="2.5" />
    <path d="M10.5 9.5 15 12l-4.5 2.5Z" />
  </svg>
);

const PlusGlyph = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden
  >
    <line x1="12" y1="5.5" x2="12" y2="18.5" />
    <line x1="5.5" y1="12" x2="18.5" y2="12" />
  </svg>
);
